import type { AgentSummary, Settings } from "@ti/core";
import { Link } from "@tanstack/react-router";
import { ArrowRight, BadgeCheck, Boxes, Building, Code2, KeyRound, Plug, ShieldCheck, Users } from "lucide-react";
import type { ReactNode } from "react";
import { CopyButton } from "@/components/domain/code";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { SetupForm } from "./credentials-step";
import type { SnippetCtx } from "./snippets";
import { Callout } from "./stepper";

export const BUILT_OPTIONS = ["platform", "inhouse"] as const;
export type Built = (typeof BUILT_OPTIONS)[number];

export const PLATFORM_OPTIONS = ["lumen-labs", "quillwork", "other"] as const;
export type PlatformOption = (typeof PLATFORM_OPTIONS)[number];

export const PLATFORM_META: Record<PlatformOption, { label: string; blurb: string; mono: string; where: string }> = {
  "lumen-labs": {
    label: "Lumen Labs",
    blurb: "Customer-service and commerce agents. Built-in traces.insure integration.",
    mono: "LL",
    where: "Workspace settings → Integrations → traces.insure",
  },
  quillwork: {
    label: "Quillwork",
    blurb: "Sales and quoting agents. Built-in traces.insure integration.",
    mono: "QW",
    where: "Agent settings → Observability → traces.insure",
  },
  other: {
    label: "Another platform",
    blurb: "Any platform with a traces.insure integration or an OpenTelemetry exporter.",
    mono: "…",
    where: "the platform's integrations or observability settings",
  },
};

export function isBuilt(v: unknown): v is Built {
  return typeof v === "string" && (BUILT_OPTIONS as readonly string[]).includes(v);
}
export function isPlatformOption(v: unknown): v is PlatformOption {
  return typeof v === "string" && (PLATFORM_OPTIONS as readonly string[]).includes(v);
}

const cardCls = (active: boolean) =>
  cn(
    "group flex flex-col gap-2 rounded-lg border bg-surface p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
    active ? "border-accent/60 bg-accent-soft/25 ring-1 ring-accent/30" : "hover:border-border-strong hover:bg-surface-2/50",
  );
const iconCls = (active: boolean) =>
  cn("grid size-8 place-items-center rounded-md font-mono text-[11px] font-bold [&_svg]:size-4", active ? "bg-accent text-accent-fg" : "bg-surface-3 text-fg-2 group-hover:text-fg");

/** Step 1: where the customer built this agent. Selection lives in the URL (`?built=`). */
export function BuiltPicker({ value }: { value: Built }) {
  const options: Array<{ id: Built; icon: ReactNode; title: string; body: string; tag?: string }> = [
    {
      id: "platform",
      icon: <Boxes />,
      title: "On an agent platform",
      body: "Lumen Labs, Quillwork or another platform. Paste your traces.insure key into the platform's built-in integration — no code.",
      tag: "No code",
    },
    {
      id: "inhouse",
      icon: <Code2 />,
      title: "In-house",
      body: "Your own code on an open framework (Claude Agent SDK, OpenAI Agents SDK, LangGraph…). Connect with an SDK or OpenTelemetry.",
    },
  ];
  return (
    <div role="radiogroup" aria-label="Where is this agent built?" className="grid gap-2 md:grid-cols-2">
      {options.map((o) => {
        const active = o.id === value;
        return (
          <Link key={o.id} from="/setup" to="/setup" search={(prev) => ({ ...prev, built: o.id })} replace resetScroll={false} role="radio" aria-checked={active} className={cardCls(active)}>
            <div className="flex items-center justify-between gap-2">
              <span aria-hidden className={iconCls(active)}>
                {o.icon}
              </span>
              {o.tag ? <Badge tone="info">{o.tag}</Badge> : null}
            </div>
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-fg">{o.title}</div>
              <p className="mt-0.5 text-xs leading-snug text-fg-3">{o.body}</p>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

/** Which platform integration on the customer's workspace matches this option (by name). */
function integrationFor(settings: Settings, p: PlatformOption) {
  if (p === "other") return undefined;
  const label = PLATFORM_META[p].label.toLowerCase();
  return settings.integrations.find((i) => i.name.toLowerCase().startsWith(label));
}

/** Step 2 (platform path): pick the agent platform (`?platform=`). */
export function PlatformPicker({ value, settings, agents }: { value: PlatformOption; settings: Settings; agents: AgentSummary[] }) {
  return (
    <div role="radiogroup" aria-label="Agent platform" className="grid gap-2 sm:grid-cols-3">
      {PLATFORM_OPTIONS.map((p) => {
        const m = PLATFORM_META[p];
        const active = p === value;
        const integration = integrationFor(settings, p);
        const count = p === "other" ? 0 : agents.filter((a) => a.platform?.slug === p).length;
        return (
          <Link key={p} from="/setup" to="/setup" search={(prev) => ({ ...prev, built: "platform" as const, platform: p })} replace resetScroll={false} role="radio" aria-checked={active} className={cardCls(active)}>
            <div className="flex items-center justify-between gap-2">
              <span aria-hidden className={iconCls(active)}>
                {m.mono}
              </span>
              {integration?.status === "connected" ? (
                <Badge tone="good">
                  <Plug /> Connected
                </Badge>
              ) : null}
            </div>
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-fg">{m.label}</div>
              <p className="mt-0.5 text-xs leading-snug text-fg-3">{m.blurb}</p>
            </div>
            {count ? (
              <div className="mt-auto text-[11px] text-fg-3">
                {count} of your agents {count === 1 ? "is" : "are"} built here
              </div>
            ) : null}
          </Link>
        );
      })}
    </div>
  );
}

function PasteRow({ label, value, secret }: { label: string; value: string; secret?: boolean }) {
  return (
    <li className="flex items-center gap-3 px-4 py-2">
      <span className="w-28 shrink-0 text-xs text-fg-3">{label}</span>
      <code className="min-w-0 flex-1 truncate font-mono text-[12px] text-fg">{value}</code>
      {secret ? <span className="text-[11px] text-fg-3">shown once on creation</span> : <CopyButton value={value} label={`Copy ${label}`} />}
    </li>
  );
}

/** Step 4 (platform path): paste the customer's key into the platform's built-in integration. */
export function PlatformConnectStep({ platform, settings, form, ctx }: { platform: PlatformOption; settings: Settings; form: SetupForm; ctx: SnippetCtx }) {
  const m = PLATFORM_META[platform];
  const name = platform === "other" ? "your platform" : m.label;
  const integration = integrationFor(settings, platform);
  // Prefer a key for this environment (live keys for prod). Platform keys are the platform's own.
  const envPrefix = form.environment === "prod" ? "ti_live" : "ti_test";
  const ingestKeys = settings.apiKeys.filter((k) => k.scopes.includes("ingest") && !k.platformId);
  const key = [...ingestKeys].sort((x, y) => Number(y.prefix.startsWith(envPrefix)) - Number(x.prefix.startsWith(envPrefix)))[0];
  const platformKeys = settings.apiKeys.filter((k) => k.platformId);
  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
      <Card>
        <CardHeader
          title={`In ${name}`}
          description={platform === "other" ? "Most platforms offer a traces.insure integration or an OpenTelemetry exporter." : `Open ${m.where}.`}
          actions={
            integration ? (
              <Badge tone={integration.status === "connected" ? "good" : integration.status === "error" ? "critical" : "neutral"}>
                <Plug /> {integration.status === "connected" ? "Connected" : integration.status === "error" ? "Error" : "Not connected"}
              </Badge>
            ) : null
          }
        />
        <CardBody>
          <ol className="flex list-decimal flex-col gap-2 pl-5 text-[13px] text-fg-2 marker:text-fg-3 [&_code]:font-mono [&_code]:text-[12px] [&_code]:text-fg">
            {platform === "other" ? (
              <>
                <li>Find the traces.insure integration in the platform&apos;s integrations or observability settings.</li>
                <li>Paste your key and the agent name. No integration? Point its OpenTelemetry exporter at the ingest endpoint with the key as a bearer token.</li>
              </>
            ) : (
              <>
                <li>
                  Open <span className="text-fg">{m.where}</span>.
                </li>
                <li>Paste your traces.insure key, the agent name and the environment.</li>
              </>
            )}
            <li>
              Set up the platform&apos;s policies for side-effecting tools (payments, account changes, deployments, data deletes). The platform enforces them and reports each
              outcome on the tool span.
            </li>
            <li>Save. Every run of the agent is exported with its model calls, tools and, where there is one, its end user.</li>
          </ol>
          <Callout icon={<Users />} className="mt-4">
            <span className="font-medium text-fg">Your FDE can do this in your workspace.</span> Forward-deployed engineers from {platform === "other" ? "your platform" : m.label} who are members of
            your traces.insure workspace can create the key and configure the integration for you. The key, the agents&apos; scores and the policies stay yours.
            <div className="mt-2 flex flex-wrap gap-2">
              <Button variant="outline" size="sm" asChild>
                <Link to="/settings" search={{ tab: "members" }}>
                  <Users /> Members
                </Link>
              </Button>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/settings" search={{ tab: "integrations" }}>
                  <Plug /> Integrations
                </Link>
              </Button>
            </div>
          </Callout>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Values to paste"
          description={`From your traces.insure workspace into ${name}.`}
          actions={
            <Button variant="outline" size="sm" asChild>
              <Link to="/settings" search={{ tab: "api-keys" }}>
                <KeyRound /> Manage keys
              </Link>
            </Button>
          }
        />
        <ul className="divide-y border-t">
          <PasteRow label="API key" value={key ? `${key.prefix}…` : "Create a key with the ingest scope"} secret />
          <PasteRow label="Agent name" value={ctx.agent} />
          <PasteRow label="Environment" value={form.environment} />
          <PasteRow label="Privacy mode" value={form.privacyMode} />
          {platform === "other" ? (
            <>
              <PasteRow label="Ingest endpoint" value={ctx.ingestBase} />
            </>
          ) : null}
        </ul>
        <CardBody className="border-t">
          <div className="flex items-start gap-2 text-xs text-fg-3 [&_svg]:mt-px [&_svg]:size-3.5 [&_svg]:shrink-0">
            <ShieldCheck />
            <span>
              Use a key with the <span className="font-mono text-fg-2">ingest</span> scope. It belongs to your workspace — revoke it any time and the platform stops exporting.
            </span>
          </div>
          <div className="mt-3 flex items-start gap-2 text-xs text-fg-3 [&_svg]:mt-px [&_svg]:size-3.5 [&_svg]:shrink-0">
            <BadgeCheck />
            <span>
              {platformKeys.length ? (
                <>
                  Platform key issued to <span className="font-mono text-fg-2">{[...new Set(platformKeys.map((k) => k.platformId))].join(", ")}</span>. Control outcomes it reports count
                  toward control adoption.
                </>
              ) : (
                <>Control outcomes count only when the platform sends them with its own platform key. traces.insure issues it: contact us to connect your platform.</>
              )}
            </span>
          </div>
          {platform === "other" ? (
            <div className="mt-3 flex items-start gap-2 text-xs text-fg-3 [&_svg]:mt-px [&_svg]:size-3.5 [&_svg]:shrink-0">
              <Building />
              <span>
                Exporter only?{" "}
                <Link to="/setup" search={{ built: "inhouse", path: "otel" }} className="text-accent hover:underline">
                  OpenTelemetry setup <ArrowRight className="inline size-3" />
                </Link>
              </span>
            </div>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}
